import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  FileText,
  Wallet,
  Calendar,
  Scissors,
  ChevronRight,
  Send,
  CircleDollarSign,
} from "lucide-react";
import { getCustomerQuotes, getAppSettings, markPayment, listCustomers } from "@/lib/api";
import { shareInvoicePdf } from "@/lib/share";

const fmtMoney = (n) => `€${(n || 0).toFixed(2)}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const Pill = ({ label, color = "zinc", testId }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-[0.16em] font-semibold border bg-${color}-50 border-${color}-200 text-${color}-700`}
    data-testid={testId}
  >
    {label}
  </span>
);

const CustomerDetail = () => {
  const { name } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // When a customer has been "split" via a per-quote MN override, the
  // Customers list link includes `?override=<N>` so we know which sub-row
  // to load. Falsy = the default customer for this name.
  const overrideParam = searchParams.get("override");
  const overrideNum =
    overrideParam && /^\d+$/.test(overrideParam) ? Number(overrideParam) : null;
  const decodedName = decodeURIComponent(name || "");
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({});
  const [sendingId, setSendingId] = useState(null);
  const [customerInfo, setCustomerInfo] = useState(null);

  useEffect(() => {
    getCustomerQuotes(decodedName, overrideNum)
      .then(setQuotes)
      .catch(() => setQuotes([]))
      .finally(() => setLoading(false));
    getAppSettings()
      .then(setSettings)
      .catch(() => {});
    listCustomers()
      .then((all) => {
        // Match BOTH the name AND override so split customers show their
        // own MN/totals, not the default Kelly's.
        setCustomerInfo(
          all.find(
            (c) =>
              (c.name || "").toLowerCase() === decodedName.toLowerCase() &&
              (c.customer_number_override ?? null) === overrideNum
          ) || null
        );
      })
      .catch(() => {});
  }, [decodedName, overrideNum]);

  const handleResend = async (e, q) => {
    // The whole row is a <Link> — don't navigate when the button is tapped.
    e.preventDefault();
    e.stopPropagation();
    if (sendingId) return;
    setSendingId(q.id);
    try {
      await shareInvoicePdf(
        q,
        settings,
        customerInfo?.customer_number_label || ""
      );
    } finally {
      setSendingId(null);
    }
  };

  // Optimistically merge the latest payment fields back into the quote row.
  const updateQuote = (id, patch) => {
    setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, ...patch } : q)));
  };

  const handleMarkDeposit = async (e, q) => {
    e.preventDefault();
    e.stopPropagation();
    const suggested = q.total ? (q.total * 0.3).toFixed(2) : "";
    const input = window.prompt(
      `Deposit amount paid (€) — leave blank for none:`,
      suggested
    );
    if (input === null) return;
    const amount = input.trim() === "" ? null : Number(input);
    if (input.trim() !== "" && (isNaN(amount) || amount < 0)) {
      toast.error("Invalid amount");
      return;
    }
    try {
      const resp = await markPayment(q.id, "deposit", amount);
      updateQuote(q.id, {
        deposit_paid_at: resp.deposit_paid_at,
        deposit_amount: resp.deposit_amount,
      });
      toast.success("Deposit recorded");
    } catch {
      toast.error("Failed to record deposit");
    }
  };

  const handleMarkPaid = async (e, q) => {
    e.preventDefault();
    e.stopPropagation();
    const ok = window.confirm(
      `Mark this quote as PAID IN FULL (€${(q.total || 0).toFixed(2)})?`
    );
    if (!ok) return;
    try {
      const resp = await markPayment(q.id, "final", null);
      updateQuote(q.id, { final_paid_at: resp.final_paid_at });
      toast.success("Marked paid in full");
    } catch {
      toast.error("Failed");
    }
  };

  const summary = useMemo(() => {
    let totalQuoted = 0;
    let depositsPaid = 0;
    let fullyPaid = 0;
    let invoiceCount = 0;
    quotes.forEach((q) => {
      totalQuoted += q.total || 0;
      if (q.deposit_paid_at) depositsPaid += q.deposit_amount || 0;
      if (q.final_paid_at) fullyPaid += q.total || 0;
      if (q.invoice_number) invoiceCount += 1;
    });
    const balance = Math.max(0, totalQuoted - depositsPaid - fullyPaid);
    return { totalQuoted, depositsPaid, fullyPaid, balance, invoiceCount };
  }, [quotes]);

  return (
    <div className="p-6 md:p-10 max-w-5xl" data-testid="customer-detail">
      <button
        onClick={() => navigate("/customers")}
        className="text-xs uppercase tracking-[0.18em] font-semibold text-zinc-500 hover:text-[#09090b] inline-flex items-center gap-1.5 mb-6"
        data-testid="back-to-customers"
      >
        <ArrowLeft size={14} /> Customers
      </button>

      <div className="label-xs">Customer</div>
      <div className="flex items-center gap-4 mt-1 flex-wrap">
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.9]">
          {decodedName}
        </h1>
        {customerInfo?.customer_number_label ? (
          <div
            className="bg-[#09090b] text-white font-mono px-3 py-2 inline-flex flex-col items-center"
            data-testid="customer-detail-number"
          >
            <span className="text-zinc-400 text-[8px] uppercase tracking-[0.18em]">
              No.
            </span>
            <span className="text-base sm:text-lg font-bold tabular-nums tracking-[0.12em]">
              {customerInfo.customer_number_label}
            </span>
          </div>
        ) : null}
      </div>

      {/* Roll-up tiles */}
      <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200">
        <div className="bg-white p-4">
          <div className="label-xs">Quoted</div>
          <div className="font-display text-2xl sm:text-3xl font-black tracking-tighter tabular-nums mt-1">
            {fmtMoney(summary.totalQuoted)}
          </div>
        </div>
        <div className="bg-white p-4">
          <div className="label-xs">Deposits in</div>
          <div className="font-display text-2xl sm:text-3xl font-black tracking-tighter tabular-nums mt-1 text-[#005bea]">
            {fmtMoney(summary.depositsPaid)}
          </div>
        </div>
        <div className="bg-white p-4">
          <div className="label-xs">Paid in full</div>
          <div className="font-display text-2xl sm:text-3xl font-black tracking-tighter tabular-nums mt-1 text-[#16a34a]">
            {fmtMoney(summary.fullyPaid)}
          </div>
        </div>
        <div className="bg-white p-4">
          <div className="label-xs">Balance</div>
          <div
            className={`font-display text-2xl sm:text-3xl font-black tracking-tighter tabular-nums mt-1 ${
              summary.balance > 0 ? "text-[#ff5722]" : "text-zinc-400"
            }`}
            data-testid="customer-balance"
          >
            {fmtMoney(summary.balance)}
          </div>
        </div>
      </div>

      <div className="font-mono text-xs text-zinc-500 mt-3">
        {quotes.length} quote{quotes.length === 1 ? "" : "s"} ·{" "}
        {summary.invoiceCount} invoice{summary.invoiceCount === 1 ? "" : "s"} issued
      </div>

      {/* Quote list */}
      <div className="mt-8">
        {loading ? (
          <div className="font-mono text-xs text-zinc-400 uppercase tracking-[0.18em] py-10 text-center">
            Loading…
          </div>
        ) : quotes.length === 0 ? (
          <div className="border border-dashed border-zinc-300 p-10 text-center">
            <div className="font-display text-xl font-black tracking-tighter">
              No quotes for this customer
            </div>
          </div>
        ) : (
          <div className="border border-zinc-200">
            <div className="hidden sm:grid grid-cols-[minmax(0,1fr)_90px_140px_220px_140px] gap-3 px-5 py-3 bg-[#f4f4f5] border-b border-zinc-200">
              <div className="label-xs">Date · Rooms</div>
              <div className="label-xs text-right">Quoted</div>
              <div className="label-xs">Deposit</div>
              <div className="label-xs">Status</div>
              <div className="label-xs">Invoice</div>
            </div>
            <ul className="divide-y divide-zinc-100">
              {quotes.map((q) => {
                const balance = Math.max(
                  0,
                  (q.total || 0) -
                    (q.deposit_paid_at ? q.deposit_amount || 0 : 0) -
                    (q.final_paid_at ? q.total || 0 : 0)
                );
                return (
                  <li key={q.id}>
                    <Link
                      to={`/quotes/${q.id}`}
                      className="block sm:grid sm:grid-cols-[minmax(0,1fr)_90px_140px_220px_140px] sm:gap-3 sm:items-center px-5 py-4 hover:bg-zinc-50 transition-colors"
                      data-testid={`customer-quote-${q.id}`}
                    >
                      <div className="min-w-0">
                        <div className="font-mono text-[11px] text-zinc-400">
                          {fmtDate(q.created_at)}
                        </div>
                        <div className="font-display text-lg font-black tracking-tighter truncate mt-0.5">
                          {(q.rooms || []).length} rooms ·{" "}
                          {q.area || <span className="text-zinc-400">no area</span>}
                        </div>
                      </div>
                      <div className="font-mono text-base sm:text-right tabular-nums mt-2 sm:mt-0">
                        {fmtMoney(q.total || 0)}
                      </div>
                      <div className="font-mono text-xs mt-1 sm:mt-0">
                        {q.deposit_paid_at ? (
                          <span className="text-[#005bea] inline-flex items-center gap-1">
                            <Wallet size={11} /> {fmtMoney(q.deposit_amount || 0)}
                          </span>
                        ) : !q.final_paid_at ? (
                          <button
                            type="button"
                            onClick={(e) => handleMarkDeposit(e, q)}
                            data-testid={`mark-deposit-${q.id}`}
                            title="Mark deposit paid"
                            className="inline-flex items-center gap-1 px-2 py-1 border border-[#005bea] text-[#005bea] hover:bg-[#005bea] hover:text-white text-[10px] uppercase tracking-[0.16em] font-semibold transition-colors"
                          >
                            <Wallet size={10} /> Mark deposit
                          </button>
                        ) : (
                          <span className="text-zinc-400">—</span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5 mt-1 sm:mt-0">
                        {q.final_paid_at ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#16a34a] text-white text-[10px] uppercase tracking-[0.16em] font-semibold">
                            <CheckCircle2 size={10} /> Paid
                          </span>
                        ) : balance > 0 && q.deposit_paid_at ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#ff5722] text-white text-[10px] uppercase tracking-[0.16em] font-semibold">
                            Bal {fmtMoney(balance)}
                          </span>
                        ) : q.confirmed_at ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#005bea] text-white text-[10px] uppercase tracking-[0.16em] font-semibold">
                            Confirmed
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-zinc-200 text-zinc-700 text-[10px] uppercase tracking-[0.16em] font-semibold">
                            Draft
                          </span>
                        )}
                        {!q.final_paid_at && balance > 0 ? (
                          <button
                            type="button"
                            onClick={(e) => handleMarkPaid(e, q)}
                            data-testid={`mark-paid-${q.id}`}
                            title={`Mark paid in full (${fmtMoney(q.total || 0)})`}
                            className="inline-flex items-center gap-1 px-2 py-1 border border-[#16a34a] text-[#16a34a] hover:bg-[#16a34a] hover:text-white text-[10px] uppercase tracking-[0.16em] font-semibold transition-colors"
                          >
                            <CircleDollarSign size={10} /> Mark paid
                          </button>
                        ) : null}
                        {q.scheduled_cut_date && !q.produced_at ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#7c3aed] text-white text-[10px] uppercase tracking-[0.16em] font-semibold">
                            <Calendar size={10} /> {q.scheduled_cut_date}
                          </span>
                        ) : null}
                        {q.produced_at ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-zinc-700 text-white text-[10px] uppercase tracking-[0.16em] font-semibold">
                            <Scissors size={10} /> Cut
                          </span>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-2 mt-1 sm:mt-0">
                        {q.invoice_number ? (
                          <button
                            type="button"
                            onClick={(e) => handleResend(e, q)}
                            disabled={sendingId === q.id}
                            data-testid={`resend-invoice-${q.id}`}
                            title={`Re-send invoice ${q.invoice_number} via WhatsApp`}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-[#0f766e] hover:bg-[#115e59] disabled:opacity-60 text-white text-[10px] uppercase tracking-[0.16em] font-semibold transition-colors"
                          >
                            {sendingId === q.id ? (
                              <>
                                <FileText size={10} /> Sending…
                              </>
                            ) : (
                              <>
                                <Send size={10} /> {q.invoice_number}
                              </>
                            )}
                          </button>
                        ) : (
                          <span className="font-mono text-[11px] text-zinc-400">
                            —
                          </span>
                        )}
                        <ChevronRight
                          size={14}
                          className="text-zinc-400 hidden sm:block"
                        />
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerDetail;
